import React, { useEffect, useState } from "react";
import classNames from "classnames";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import Grid from "@material-ui/core/Grid";
import UiButton from "../common/UiButton";
import { tableTheme, tableToolbarControlHeight, tableToolbarContentHeight, tableToolbarGroupCornerRight } from "./tableTheme";

const styles = (theme: Theme) =>
  createStyles({
    input: {
      borderRadius: "3px 0 0 3px",
      padding: 7,
      fontSize: 17,
      borderTop: "1px solid rgba(72,72,72,.2)",
      borderLeft: "1px solid rgba(72,72,72,.2)",
      borderBottom: "1px solid rgba(72,72,72,.2)",
      borderRight: "none",
      width: 200,
      outline: "none",
      boxShadow: "none",
      "&:focus": {
        outline: "none",
        boxShadow: "none",
      },
      "&::placeholder": {
        color: "#bbb",
        opacity: 1,
      },
      "&::-webkit-input-placeholder": {
        color: "#bbb",
      },
      "&::-moz-placeholder": {
        color: "#bbb",
        opacity: 1,
      },
      "&:-ms-input-placeholder": {
        color: "#bbb",
      },
    },
    inputModern: {
      borderRadius: `${tableTheme.radiusSm}px 0 0 ${tableTheme.radiusSm}px`,
      padding: "4px 10px",
      fontSize: "0.8125rem",
      height: tableToolbarControlHeight,
      lineHeight: `${tableToolbarContentHeight}px`,
      boxSizing: "border-box",
      borderTop: `1px solid ${tableTheme.border}`,
      borderLeft: `1px solid ${tableTheme.border}`,
      borderBottom: `1px solid ${tableTheme.border}`,
      borderRight: "none",
      width: 200,
      outline: "none",
      boxShadow: "none",
      color: tableTheme.textPrimary,
      "&:focus": {
        outline: "none",
        boxShadow: "none",
        borderColor: tableTheme.secondaryLight,
      },
      "&::placeholder": {
        color: tableTheme.textSecondary,
        opacity: 1,
      },
    },
    button: {
      borderRadius: "0 3px 3px 0",
      padding: 8,
      fontSize: 17,
      borderTop: "1px solid rgba(72,72,72,.2)",
      borderRight: "1px solid rgba(72,72,72,.2)",
      borderBottom: "1px solid rgba(72,72,72,.2)",
      width: 50
    },
    buttonModern: {
      borderRadius: `0 ${tableTheme.radiusSm}px ${tableTheme.radiusSm}px 0`,
      padding: 4,
      minWidth: 36,
      width: 36,
      height: tableToolbarControlHeight,
      minHeight: tableToolbarControlHeight,
      borderTop: `1px solid ${tableTheme.border}`,
      borderRight: `1px solid ${tableTheme.border}`,
      borderBottom: `1px solid ${tableTheme.border}`,
      borderLeft: "none",
    },
    formInGroup: {
      display: "flex",
      alignItems: "stretch",
      height: "100%",
      width: "100%",
    },
    inputInGroup: {
      border: "none",
      borderRadius: 0,
      padding: "4px 10px",
      fontSize: "0.8125rem",
      height: "100%",
      minHeight: "100%",
      lineHeight: `${tableToolbarContentHeight}px`,
      boxSizing: "border-box",
      flex: "1 1 auto",
      width: "auto",
      minWidth: 160,
      outline: "none",
      boxShadow: "none",
      color: tableTheme.textPrimary,
      backgroundColor: tableTheme.surface,
      "&:focus": {
        outline: "none",
        boxShadow: "none",
      },
      "&::placeholder": {
        color: tableTheme.textSecondary,
        opacity: 1,
      },
    },
    buttonInGroup: {
      border: "none",
      borderLeft: `1px solid ${tableTheme.border}`,
      padding: 4,
      minWidth: 36,
      width: 36,
      height: "100%",
      minHeight: "100%",
      flexShrink: 0,
      ...tableToolbarGroupCornerRight,
    },
  });

interface PropTypes {
  classes: any;
  setSearchString: any;
  searchString: string;
  variant?: "default" | "modern";
  inGroup?: boolean;
}

const UiSearchInputField = (props: PropTypes) => {
  const {
    classes,
    setSearchString,
    searchString,
    variant = "default",
    inGroup = false,
  } = props;
  const isModern = variant === "modern";
  const isGrouped = isModern && inGroup;
  const [value, setValue] = useState<string>(searchString);

  const onSubmit = (e: any) => {
    e.preventDefault();
    if (value === searchString && value !== "") {
      setValue("");
      setSearchString("");
      return;
    }
    setSearchString(value);
  };

  useEffect(() => {
    setValue(searchString);
  }, [searchString]);

  const onChange = (e: any) => setValue(e.target.value);

  return (
    <Grid
      container
      direction="row"
      className={classNames(classes.searchInput, {
        [classes.formInGroup]: isGrouped,
      })}
      component="form"
      onSubmit={onSubmit}
    >
      {
        //@ts-ignore
        <Grid item component="input" name="recherche"
          className={classNames({
            [classes.input]: !isModern,
            [classes.inputModern]: isModern && !isGrouped,
            [classes.inputInGroup]: isGrouped,
          })}
          placeholder="Rechercher..." value={value} onChange={onChange}/>
      }
      <Grid item>
        <UiButton
          color={
            searchString === "" || searchString !== value
              ? "secondary"
              : "primary"
          }
          variant="contained"
          className={classNames({
            [classes.button]: !isModern,
            [classes.buttonModern]: isModern && !isGrouped,
            [classes.buttonInGroup]: isGrouped,
          })}
          type="submit"
        >
          {(searchString === "" || searchString !== value) && (
            <SearchIcon fontSize={isModern ? "small" : "default"} />
          )}
          {searchString !== "" && searchString === value && (
            <ClearIcon fontSize={isModern ? "small" : "default"} />
          )}
        </UiButton>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(UiSearchInputField);
